import { useEffect, useState } from 'react'
import { createTheme, defaultLightTheme, type Theme } from '@openuidev/react-ui/ThemeProvider'
import { agentMauriceChartPalette } from './chartPalette'

export { agentMauriceChartPalette } from './chartPalette'

export type OpenUIColorMode = 'light' | 'dark'

// The OS switches theme with the `dark` class on <html>.
export function currentOSColorMode(): OpenUIColorMode {
  if (typeof document === 'undefined') return 'light'
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light'
}

export function useOSColorMode(): OpenUIColorMode {
  const [mode, setMode] = useState<OpenUIColorMode>(currentOSColorMode)
  useEffect(() => {
    const update = () => setMode(currentOSColorMode())
    update()
    if (typeof MutationObserver === 'undefined') return undefined
    const observer = new MutationObserver(update)
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [])
  return mode
}

const token = (name: string) => `hsl(var(--${name}))`
// Accent states derived from the token (os-aurora-migration § 4.1): mixing
// with --foreground darkens the violet in light and lightens the cyan at night.
const accentShade = (percent: number) => `color-mix(in srgb, hsl(var(--primary)) ${100 - percent}%, hsl(var(--foreground)))`

// Aurora correspondence of every OpenUI colour key (visual-identity,
// Correspondance du thème OpenUI; os-aurora-migration § 4.1). The OpenUI
// components read --openui-* variables; each one points at an OS token
// (@agent-maurice/ui tokens imported by src/styles/index.css), which already
// switch with the `dark` class. In OpenUI, "background" is the page and
// "foreground" a raised surface (cards, tooltips), not a text colour.
//
// `elevated*` use --muted, not --card-raised: at night --muted-foreground on
// --card-raised is 4.40:1 (below 4.5:1), on --muted it passes; in light both
// tokens are equal.
const osOpenUIColors: Theme = {
  // Surfaces
  background: token('background'),
  sunk: token('background'),
  sunkLight: token('background'),
  sunkDeep: token('background'),
  foreground: token('card'),
  popoverBackground: token('card'),
  elevatedLight: token('muted'),
  elevated: token('muted'),
  elevatedStrong: token('muted'),
  elevatedIntense: token('muted'),
  overlay: 'hsl(var(--foreground) / 0.4)',
  highlightSubtle: token('card-raised'),
  highlight: token('card-raised'),
  highlightStrong: token('card-raised'),
  highlightIntense: token('border'),
  invertedBackground: token('foreground'),
  successBackground: token('success-soft'),
  alertBackground: token('warning-soft'),
  dangerBackground: token('error-soft'),
  infoBackground: token('info-soft'),
  purpleBackground: token('primary-soft'),
  pinkBackground: token('primary-soft'),

  // Text: --muted-foreground is the readability floor, no third grey.
  textNeutralPrimary: token('foreground'),
  textNeutralSecondary: token('muted-foreground'),
  textNeutralTertiary: token('muted-foreground'),
  textNeutralLink: token('link'),
  textBrand: token('primary'),
  textWhite: token('primary-foreground'),
  textBlack: token('foreground'),
  textAccentPrimary: token('primary-foreground'),
  textAccentSecondary: token('primary-foreground'),
  textAccentTertiary: token('primary-foreground'),
  textSuccessPrimary: token('success'),
  textSuccessInverted: token('success-foreground'),
  textAlertPrimary: token('warning'),
  textAlertInverted: token('warning-foreground'),
  textDangerPrimary: token('error'),
  textDangerSecondary: token('error'),
  textDangerTertiary: token('error'),
  textDangerInvertedPrimary: token('error-foreground'),
  textDangerInvertedSecondary: token('error-foreground'),
  textDangerInvertedTertiary: token('error-foreground'),
  textInfoPrimary: token('link'),
  textInfoInverted: token('info-foreground'),
  textPinkPrimary: token('accent-3'),
  textPinkInverted: token('primary-foreground'),
  textPurplePrimary: token('accent-2'),
  textPurpleInverted: token('primary-foreground'),

  // Interactive states
  interactiveAccentDefault: token('primary'),
  interactiveAccentHover: accentShade(8),
  interactiveAccentPressed: accentShade(14),
  interactiveAccentDisabled: 'hsl(var(--primary) / 0.5)',
  interactiveDestructiveDefault: token('card'),
  interactiveDestructiveHover: token('error-soft'),
  interactiveDestructivePressed: token('error-soft'),
  interactiveDestructiveDisabled: token('card'),
  interactiveDestructiveAccentDefault: token('destructive'),
  interactiveDestructiveAccentHover: token('destructive'),
  interactiveDestructiveAccentPressed: token('destructive'),
  interactiveDestructiveAccentDisabled: 'hsl(var(--destructive) / 0.5)',

  // Chat (unused by the MiniApp catalogue, linked for completeness)
  chatUserResponseBg: token('card-raised'),
  chatUserResponseText: token('foreground'),

  // Lines and focus
  borderDefault: token('border'),
  borderInteractive: token('border'),
  borderInteractiveEmphasis: token('input'),
  borderInteractiveSelected: token('ring'),
  borderAccent: token('border'),
  borderAccentEmphasis: token('ring'),
  borderAccentSelected: token('ring'),
  borderInfo: token('border'),
  borderInfoEmphasis: token('info'),
  borderAlert: token('border'),
  borderAlertEmphasis: token('warning'),
  borderSuccess: token('border'),
  borderSuccessEmphasis: token('success'),
  borderDanger: token('border'),
  borderDangerEmphasis: token('error'),
}

// Flat rendering: no shadow on the OS (OSA-03).
const osOpenUIEffects: Theme = {
  shadow0: 'none',
  shadowS: 'none',
  shadowM: 'none',
  shadowL: 'none',
  shadowXl: 'none',
  shadow2xl: 'none',
  shadow3xl: 'none',
}

// Typography: content in Manrope (--font-sans), code in JetBrains Mono. The
// OpenUI compound `font:` tokens embed their family, so each one is rewritten
// from the OpenUI default with the charter family. Cards are 16 px.
const OPENUI_SANS = '"Inter", sans-serif'
const OPENUI_MONO = '"SFMono-Regular", Menlo, monospace'
function charterTypography(): Theme {
  const out: Record<string, string> = {
    fontBody: 'var(--font-sans)',
    fontHeading: 'var(--font-sans)',
    fontLabel: 'var(--font-sans)',
    fontNumbers: 'var(--font-sans)',
    fontCode: 'var(--font-mono)',
    radius2xl: '16px',
  }
  for (const [key, value] of Object.entries(defaultLightTheme)) {
    if (key in out || typeof value !== 'string') continue
    if (value.includes(OPENUI_SANS)) out[key] = value.split(OPENUI_SANS).join('var(--font-sans)')
    else if (value.includes(OPENUI_MONO)) out[key] = value.split(OPENUI_MONO).join('var(--font-mono)')
  }
  return out as Theme
}

const osOpenUIBase: Theme = { ...osOpenUIColors, ...osOpenUIEffects, ...charterTypography() }

export const osOpenUILightTheme = createTheme({ ...osOpenUIBase, defaultChartPalette: agentMauriceChartPalette.light })
export const osOpenUIDarkTheme = createTheme({ ...osOpenUIBase, defaultChartPalette: agentMauriceChartPalette.dark })

// orderedPalette lays out a palette so that OpenUI's distribution, which
// samples from the middle of the palette, returns its first n slots in order.
export function orderedPalette(palette: string[], count: number): string[] {
  const n = Math.max(1, count)
  const size = 2 * n + 1
  const middle = Math.floor(size / 2)
  const layout = new Array<string>(size).fill(palette[0])
  if (n === 1) {
    layout[middle] = palette[0]
  } else if (n === 2) {
    layout[middle - 1] = palette[0]
    layout[middle + 1] = palette[1]
  } else {
    const offset = Math.floor((n - 1) / 2)
    for (let i = 0; i < n; i++) layout[middle + i - offset] = palette[i]
  }
  return layout
}
